import { create } from 'zustand'
import { supabase } from '@/services/supabase'

export type BoardType = 'conference' | 'team' | 'game' | 'topic'

export interface Board {
  id: string
  slug: string
  name: string
  type: BoardType
  teamId: string | null
  conference: string | null
  description: string | null
  iconUrl: string | null
  memberCount: number
  postCount: number
  membership: { role: string } | null
  parentId?: string | null
  subforums?: Board[]
}

interface CommunityStore {
  boards: Board[]
  lastFetchedAt: number | null
  isLoading: boolean
  error: string | null
  fetchBoards: (userId?: string) => Promise<void>
}

const API_BASE = import.meta.env.VITE_API_URL || 'https://bracketblitz-production.up.railway.app/api/v1'
const CACHE_TTL = 30_000 // 30s

export const useCommunityStore = create<CommunityStore>((set, get) => ({
  boards: [],
  lastFetchedAt: null,
  isLoading: false,
  error: null,

  fetchBoards: async (userId?: string) => {
    const { lastFetchedAt, isLoading, boards } = get()

    // SWR: if we have cached data, return it immediately and refresh in background
    const hasCachedData = boards.length > 0
    const isFresh = lastFetchedAt && Date.now() - lastFetchedAt < CACHE_TTL

    if (isFresh) return // Still fresh, skip
    if (isLoading) return // Already fetching

    // Only show loading spinner if no cached data
    if (!hasCachedData) set({ isLoading: true })

    try {
      let token: string | null = null
      if (userId) {
        const session = await supabase.auth.getSession()
        token = session?.data?.session?.access_token || null
      }

      const res = await fetch(`${API_BASE}/community/boards`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })

      if (!res.ok) {
        if (res.status === 403 || res.status === 401) {
          set({ error: 'coming_soon', isLoading: false })
          return
        }
        throw new Error('Failed')
      }

      const data = await res.json()
      const boardsList = data?.data?.boards || data?.boards || []
      set({ boards: boardsList, lastFetchedAt: Date.now(), isLoading: false, error: null })
    } catch {
      if (!hasCachedData) set({ error: 'coming_soon' })
      set({ isLoading: false })
    }
  },
}))
